/**
 * Where the API server is, as the client sees it — and where the game socket is, when the two
 * are not the same place.
 *
 * In a browser both are empty and everything is same-origin: in dev Vite proxies `/trpc`,
 * `/api`, `/auth` and `/ws` to :4380 (`vite.config.ts`), and in production the API serves the
 * client. That is what keeps the httpOnly session cookie a first-party cookie, and it is the
 * default this file must never lose.
 *
 * A NATIVE SHELL HAS NO SAME ORIGIN. The page is `capacitor://localhost` (iOS) or
 * `https://localhost` (Android), and `localhost` on a phone is the phone — so a bundled
 * Capacitor build has to be told both addresses at build time. Both are `VITE_*`, so both are
 * inlined by Vite into the bundle and frozen into whatever binary is built from it; neither is
 * a secret (they are origins every player's device dials anyway).
 *
 * They are two variables rather than one on purpose. The socket's origin is not this client's to
 * guess: `match.join` *names* it, from the server's own `PUBLIC_WS_URL` / `GAME_WORKERS`, and a
 * deployment with remote workers names a different host per room. `VITE_WS_URL` overrides that
 * answer, which is right for a single-host deployment and wrong for a fleet — see `docs/mobile.md`.
 */

/** Trim whitespace and any trailing slashes; an unset or blank variable is the empty default. */
function origin(value: string | undefined): string {
  return (value ?? '').trim().replace(/\/+$/, '');
}

/**
 * The API origin — `https://chompdome.com`, `http://192.168.1.50:4380`, or empty for same-origin.
 * Every URL this client builds for the server starts here: the tRPC link in `trpc.ts` and the
 * plain navigation that starts Google sign-in alike, so no second place writes a host down.
 */
export const API_BASE_URL = origin(import.meta.env.VITE_API_URL);

/**
 * The game socket's origin — `wss://chompdome.com`, `ws://192.168.1.50:4380`, or empty to use
 * whatever `match.join` named. Applied by `resolveWsUrl`, which keeps the *path* the matchmaker
 * chose (it carries the room id) and only replaces the origin in front of it.
 */
export const WS_BASE_URL = origin(import.meta.env.VITE_WS_URL);
